import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";

const ease = [0.22, 1, 0.36, 1] as const;

// スクロールで画面に入ったらふわっと表示する(x で横から、y で下から)
export const Reveal = ({
  children,
  delay = 0,
  x = 0,
  y = 32,
  className,
}: {
  children: React.ReactNode;
  delay?: number;
  x?: number;
  y?: number;
  className?: string;
}) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, x, y }}
    whileInView={{ opacity: 1, x: 0, y: 0 }}
    viewport={{ once: true, amount: 0.2 }}
    transition={{ duration: 0.8, delay, ease }}
  >
    {children}
  </motion.div>
);

// 小さな芽のアイコン
export const Sprout = ({ className, color = "currentColor" }: { className?: string; color?: string }) => (
  <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
    <path d="M12 22 V12" stroke={color} strokeWidth="2" strokeLinecap="round" fill="none" />
    <path d="M12 14 C6.5 14 4 10 4 6.5 C8.5 6.5 12 9 12 14 Z" fill={color} />
    <path d="M12 12 C12 7 15 3.5 20 3.5 C20 8 17 12 12 12 Z" fill={color} />
  </svg>
);

// 英字ラベル付きのセクション見出し(下に芽が出るアニメーション)
export const SectionHeading = ({
  en,
  ja,
  align = "center",
  light = false,
  x,
}: {
  en: string;
  ja: string;
  align?: "center" | "left";
  light?: boolean;
  x?: number;
}) => (
  <Reveal
    className={`tht-heading tht-heading-${align}${light ? " is-light" : ""}`}
    x={x}
    y={x ? 0 : undefined}
  >
    <span className="tht-heading-en">{en}</span>
    <h2 className="tht-heading-ja">{ja}</h2>
    <motion.span
      className="tht-heading-sprout"
      initial={{ scale: 0, rotate: -20 }}
      whileInView={{ scale: 1, rotate: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.7, delay: 0.4, ease }}
    >
      <Sprout />
    </motion.span>
  </Reveal>
);

// スクロールすると茎が伸びて葉が開く、大きな芽
export const GrowSprout = ({ className }: { className?: string }) => (
  <motion.svg
    className={className}
    viewBox="0 0 120 220"
    aria-hidden="true"
    initial="hidden"
    whileInView="show"
    viewport={{ once: true, amount: 0.4 }}
  >
    <motion.path
      d="M60 220 C60 180 52 140 62 70"
      fill="none"
      stroke="#5cae5a"
      strokeWidth="5"
      strokeLinecap="round"
      variants={{ hidden: { pathLength: 0 }, show: { pathLength: 1 } }}
      transition={{ duration: 1.4, ease }}
    />
    <motion.path
      d="M58 120 C30 122 14 100 12 78 C36 76 56 92 58 120 Z"
      fill="#7cc576"
      style={{ originX: "58px", originY: "120px" }}
      variants={{ hidden: { scale: 0 }, show: { scale: 1 } }}
      transition={{ duration: 0.8, delay: 1.0, ease }}
    />
    <motion.path
      d="M62 72 C62 40 82 16 112 12 C112 44 92 70 62 72 Z"
      fill="#5cae5a"
      style={{ originX: "62px", originY: "72px" }}
      variants={{ hidden: { scale: 0 }, show: { scale: 1 } }}
      transition={{ duration: 0.8, delay: 1.3, ease }}
    />
  </motion.svg>
);

// 地面(なだらかな丘)と、そこから生える芽のシルエット
const groundSprouts = [
  { x: 140, y: 74, s: 1 },
  { x: 205, y: 70, s: 0.7 },
  { x: 560, y: 72, s: 0.9 },
  { x: 930, y: 56, s: 1.1 },
  { x: 985, y: 52, s: 0.65 },
  { x: 1290, y: 60, s: 0.85 },
];

export const GroundEdge = ({ color, className = "" }: { color: string; className?: string }) => (
  <div className={`tht-ground ${className}`} aria-hidden="true">
    <svg viewBox="0 0 1440 120" preserveAspectRatio="none">
      {groundSprouts.map((p, i) => (
        <g key={i} transform={`translate(${p.x} ${p.y}) scale(${p.s})`}>
          <g className="tht-ground-sprout" style={{ animationDelay: `${0.4 + i * 0.15}s` }}>
            <path d="M0 6 C0 -6 2 -16 0 -30" stroke={color} strokeWidth="4" strokeLinecap="round" fill="none" />
            <path d="M0 -14 C-14 -14 -24 -24 -24 -36 C-10 -36 0 -28 0 -14 Z" fill={color} />
            <path d="M0 -26 C0 -40 10 -50 24 -50 C24 -36 14 -26 0 -26 Z" fill={color} />
          </g>
        </g>
      ))}
      <path
        d="M0 84 C180 60 360 68 560 76 C760 84 900 44 1080 50 C1240 56 1360 70 1440 64 L1440 120 L0 120 Z"
        fill={color}
      />
    </svg>
  </div>
);

// 流れる英字テキスト
export const Marquee = ({ text }: { text: string }) => (
  <div className="tht-marquee" aria-hidden="true">
    <div className="tht-marquee-track">
      {Array.from({ length: 4 }).map((_, i) => (
        <span key={i}>{text}</span>
      ))}
    </div>
  </div>
);

// 背景にふわふわ浮かぶ装飾(有機的な形・タネ・葉)
export const FloatingDecor = () => (
  <div className="tht-decor" aria-hidden="true">
    <span className="tht-blob tht-blob-1" />
    <span className="tht-blob tht-blob-2" />
    <span className="tht-seed tht-seed-1" />
    <span className="tht-seed tht-seed-2" />
    <span className="tht-seed tht-seed-3" />
    <span className="tht-leaf tht-leaf-1" />
    <span className="tht-leaf tht-leaf-2" />
  </div>
);

// 種 → 芽 → 花 と順に成長する大きなイラスト(画面に入ったら再生)
export const GrowFlower = ({ className }: { className?: string }) => {
  const step = (delay: number) => ({ duration: 0.8, delay, ease });
  const pop = { hidden: { scale: 0 }, show: { scale: 1 } };
  return (
    <motion.svg
      className={className}
      viewBox="0 0 160 300"
      aria-hidden="true"
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount: 0.3 }}
    >
      {/* 種 */}
      <motion.ellipse cx="80" cy="284" rx="12" ry="9" fill="#b5651d" style={{ originX: "80px", originY: "284px" }} variants={pop} transition={step(0)} />
      {/* 茎 */}
      <motion.path d="M80 282 C80 230 70 180 80 110" fill="none" stroke="#5cae5a" strokeWidth="6" strokeLinecap="round" variants={{ hidden: { pathLength: 0 }, show: { pathLength: 1 } }} transition={{ duration: 1.4, delay: 0.6, ease }} />
      {/* 葉 */}
      <motion.path d="M77 220 C44 222 26 198 24 172 C52 170 74 188 77 220 Z" fill="#7cc576" style={{ originX: "77px", originY: "220px" }} variants={pop} transition={step(1.4)} />
      <motion.path d="M79 176 C80 146 100 124 132 122 C132 152 110 174 79 176 Z" fill="#5cae5a" style={{ originX: "79px", originY: "176px" }} variants={pop} transition={step(1.7)} />
      {/* 花 */}
      <motion.g style={{ originX: "80px", originY: "92px" }} variants={{ hidden: { scale: 0, rotate: -90 }, show: { scale: 1, rotate: 0 } }} transition={{ duration: 1, delay: 2.1, ease }}>
        {[0, 72, 144, 216, 288].map((r) => (
          <ellipse key={r} cx="80" cy="66" rx="14" ry="24" fill="#f6a07a" transform={`rotate(${r} 80 92)`} />
        ))}
        <circle cx="80" cy="92" r="13" fill="#f2b632" />
      </motion.g>
    </motion.svg>
  );
};

// パンくずリスト:今いるページの位置を示し、上の階層へ1クリックで戻れるようにする
export const Breadcrumb = ({ items }: { items: { label: string; to?: string }[] }) => (
  <nav className="tht-breadcrumb" aria-label="パンくずリスト">
    <ol>
      {items.map((item, i) => (
        <li key={item.label}>
          {item.to ? <Link to={item.to}>{item.label}</Link> : <span aria-current="page">{item.label}</span>}
          {i < items.length - 1 && <Sprout className="tht-breadcrumb-sep" />}
        </li>
      ))}
    </ol>
  </nav>
);

// 舞う綿毛:タンポポの綿毛(=新しい場所へ旅立つタネ)がふわふわ飛んでいく
export const FloatingFluff = ({ count = 6 }: { count?: number }) => (
  <div className="tht-fluff" aria-hidden="true">
    {Array.from({ length: count }).map((_, i) => (
      <span key={i} className={`tht-fluff-item tht-fluff-${i + 1}`}>
        <svg viewBox="0 0 40 40">
          {Array.from({ length: 9 }).map((__, j) => {
            const a = (Math.PI * (j + 0.5)) / 9 + Math.PI;
            return (
              <line
                key={j}
                x1="20"
                y1="22"
                x2={20 + Math.cos(a) * 14}
                y2={22 + Math.sin(a) * 14}
                stroke="#c9b29c"
                strokeWidth="1.2"
                strokeLinecap="round"
              />
            );
          })}
          <line x1="20" y1="22" x2="20" y2="36" stroke="#c9a27a" strokeWidth="1.2" />
          <ellipse cx="20" cy="37" rx="1.6" ry="2.4" fill="#b5651d" />
        </svg>
      </span>
    ))}
  </div>
);

// 風に揺れる大きな木:幹は根元が太く、枝分かれした先に濃淡のある葉のかたまりを重ねる
const crownClusters = [
  { cx: 150, cy: 120, r: 70, c: "#3f8a3d" },
  { cx: 92, cy: 150, r: 56, c: "#4a9a47" },
  { cx: 212, cy: 150, r: 58, c: "#3f8a3d" },
  { cx: 120, cy: 86, r: 52, c: "#5cae5a" },
  { cx: 186, cy: 84, r: 50, c: "#56a653" },
  { cx: 150, cy: 52, r: 44, c: "#6dbb69" },
  { cx: 66, cy: 186, r: 38, c: "#56a653" },
  { cx: 238, cy: 190, r: 40, c: "#4a9a47" },
  { cx: 150, cy: 168, r: 48, c: "#4a9a47" },
];
const crownHighlights = [
  { cx: 118, cy: 70, r: 20 },
  { cx: 176, cy: 66, r: 16 },
  { cx: 84, cy: 136, r: 14 },
  { cx: 146, cy: 36, r: 14 },
  { cx: 206, cy: 128, r: 12 },
];

export const SwayTree = ({ className = "" }: { className?: string }) => (
  <div className={`tht-tree ${className}`} aria-hidden="true">
    <svg viewBox="0 0 300 400">
      <defs>
        <linearGradient id="thtTrunk" x1="0" x2="1">
          <stop offset="0" stopColor="#5a3a1e" />
          <stop offset="0.55" stopColor="#7a4f29" />
          <stop offset="1" stopColor="#5a3a1e" />
        </linearGradient>
      </defs>
      {/* 根元の影 */}
      <ellipse cx="150" cy="392" rx="90" ry="8" fill="rgba(62,46,34,0.18)" />
      {/* 幹と枝(根元が広がり、上に向かって細くなる) */}
      <path
        d="M118 396 C130 380 136 350 136 310 C136 270 128 240 120 210 L132 206 C142 236 148 256 150 270 C154 250 164 228 182 204 L192 212 C176 236 166 262 164 300 C164 350 170 380 186 396 Z"
        fill="url(#thtTrunk)"
      />
      <path d="M138 250 C118 232 98 214 80 190" stroke="#6a4424" strokeWidth="9" strokeLinecap="round" fill="none" />
      <path d="M162 240 C186 222 208 206 226 186" stroke="#6a4424" strokeWidth="8" strokeLinecap="round" fill="none" />
      <path d="M148 272 C142 300 144 340 146 380" stroke="#4a2e16" strokeWidth="2" opacity="0.35" fill="none" />
      {/* 葉のかたまり(揺れる部分) */}
      <g className="tht-tree-crown">
        {crownClusters.map((c, i) => (
          <circle key={i} cx={c.cx} cy={c.cy} r={c.r} fill={c.c} />
        ))}
        {crownHighlights.map((h, i) => (
          <circle key={`h${i}`} cx={h.cx} cy={h.cy} r={h.r} fill="#8fd08a" opacity="0.55" />
        ))}
      </g>
    </svg>
    <span className="tht-tree-leaf" />
    <span className="tht-tree-leaf" />
    <span className="tht-tree-leaf" />
    <span className="tht-tree-leaf" />
  </div>
);

// しゃぼん玉:ふわふわ揺れながら上っていく(お問い合わせ:思いがふわりと届くイメージ)
export const Bubbles = ({ count = 10 }: { count?: number }) => (
  <div className="tht-bubbles" aria-hidden="true">
    {Array.from({ length: count }).map((_, i) => (
      <span key={i} className={`tht-bubble tht-bubble-${i + 1}`} />
    ))}
  </div>
);

// 光の粒:ゆっくり漂いながら、ふっと明るくなったり消えたりする(プライバシーポリシー:落ち着いた安心感)
export const Glows = ({ count = 14 }: { count?: number }) => (
  <div className="tht-glows" aria-hidden="true">
    {Array.from({ length: count }).map((_, i) => (
      <span key={i} className={`tht-glow tht-glow-${i + 1}`} />
    ))}
  </div>
);

// ページのタイトル・説明文・正式なURLを設定する
// (外部の react-document-meta は古い書き方のため、Reactの警告が出る。その代わりに使う)
type PageMetaProps = { title: string; description?: string; canonical?: string };
const setMetaTag = (selector: string, create: () => HTMLElement, attr: string, value?: string) => {
  if (!value) return;
  let el = document.head.querySelector<HTMLElement>(selector);
  if (!el) {
    el = create();
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
};
export const PageMeta = ({ title, description, canonical }: PageMetaProps) => {
  React.useEffect(() => {
    document.title = title;
    setMetaTag('meta[name="description"]', () => {
      const m = document.createElement("meta");
      m.setAttribute("name", "description");
      return m;
    }, "content", description);
    setMetaTag('link[rel="canonical"]', () => {
      const l = document.createElement("link");
      l.setAttribute("rel", "canonical");
      return l;
    }, "href", canonical);
  }, [title, description, canonical]);
  return null;
};
